import { z } from 'zod/v4';

import {
    BACKGROUND_LAYER_SEEDS,
    compactBlockProps,
    createDefaultBlock,
    createShapeDivider,
    type DefaultBlock,
} from '@sites/entity-core';

import type { IgnoredField } from '../validator/forgiving-validator.type';
import {
    boxPatchShape,
    everyDevice,
    foldBoxPatch,
    mentions,
    patchEveryDevice,
    RENAMED_BOX_KEYS,
    scrollEffectShape,
    THEME_COLOR_HINT,
    type BoxPatch,
    type PropsBag,
} from './props-patch';

/**
 * AI-facing schemas for the `create_block` / `update_block` tools, and the fold that lands them on a
 * v0 block's `props`.
 *
 * The entity itself is `@sites/entity-core`'s — `blockDefaultV0Schema` and the legacy migration
 * behind `blockRegistry`. This file used to carry a hand-written legacy pass-through block schema
 * instead, because entity-core's `block/` slot was mid-redesign and validating-by-rewriting was the
 * opposite of what a read-modify-write needs. That redesign has landed: v0 is the shape the editor
 * stores and the migration is what a legacy page is read through, so there is nothing left for a
 * second block schema to protect and the duplicate is gone.
 *
 * FLAT keys, legacy vocabulary — see `props-patch.ts` for why.
 */

/**
 * The AI's default block. Mirrors the editor's own `blockInstance()`
 * (`apps/websites/.../blocks/blocks.models.ts`): 100px top/bottom padding and the light theme
 * background, with the contour, the dividers and the background layers off — so they keep their
 * switches alone, and switching one on seeds it the way the editor does. A block the model creates
 * is indistinguishable from one the user added by hand.
 */
export function createAiDefaultBlock(): DefaultBlock {
    const block = createDefaultBlock();

    return {
        ...block,
        props: compactBlockProps({
            ...block.props,
            padding: everyDevice(() => ({ value: { top: 100, right: 0, bottom: 100, left: 0 }, unit: 'px' as const })),
            background: { ...block.props.background, color: everyDevice(() => 'light') },
        }),
    };
}

/**
 * Keys a v0 block does not store: a shape the surface no longer accepts, something a v0 block derives
 * rather than stores, or a setting belonging to a subtype the AI must not restyle.
 *
 * **Reasons, not declarations.** They used to be fields in `blockPatchShape` too
 * (`z.unknown().optional().describe('Accepted but NOT applied: …')`), purely so the fold could name
 * them instead of `z.object()` stripping them in silence. Forty such declarations across the four
 * block/column schemas rode to the model on every step for that — 5.5 KB of a 143 KB tool surface —
 * and once `validateForgiving` began reporting unknown keys off the schema's own shape, the only
 * thing they still bought was the wording below. So the map is handed to it as `unknownKeyReasons`
 * and nothing here reaches the model.
 *
 * `data`/`styles` are the bucketed shape the surface carried before the settings went flat. Support
 * for it is GONE — the buckets are no longer lifted onto the flat args — but they stay listed here,
 * because removing them from the shape entirely is what would make the shape dangerous: `z.object`
 * strips an undeclared key with no issue raised, so a bucketed create would build a default block
 * and report success for styling that was never applied.
 *
 * The header/canvas settings are refused on purpose: a header block is SHARED across every page of
 * the site (`meta.isShared`), so restyling one from a page-scoped chat turn changes pages the user
 * was not looking at.
 */
export const IGNORED_BLOCK_KEYS = {
    data: 'block settings are FLAT top-level arguments — send them directly, not wrapped in "data"',
    styles: 'block settings are FLAT top-level arguments — send them directly, not wrapped in "styles"',
    isIndependentBorders: 'derived from the four sides in v0 — set the per-side border keys instead',
    backgroundImage: 'a background image must be picked from the media library, which this tool cannot do',
    backgroundVideo: 'a background video must be picked from the media library, which this tool cannot do',
    hideOverflow: 'canvas-block setting; not editable through this tool',
    iconHorizontalPosition: 'header-block setting; headers are shared across pages and not editable here',
    iconSize: 'header-block setting; headers are shared across pages and not editable here',
    iconColor: 'header-block setting; headers are shared across pages and not editable here',
    textColor: 'header-block setting; headers are shared across pages and not editable here',
    horizontalPosition: 'header-block setting; headers are shared across pages and not editable here',
    hideContentOnMobile: 'header-block setting; headers are shared across pages and not editable here',
    hasSeparator: 'header-block setting; headers are shared across pages and not editable here',
    hideWidgetsOnMobile: 'header-block setting; headers are shared across pages and not editable here',
    pinOnTheTop: 'renamed — send pin: "top" (or null to unpin)',
    pinOnTheBottom: 'renamed — send pin: "bottom" (or null to unpin)',
    shared: 'renamed — send role: "header" | "footer"',
    ...RENAMED_BOX_KEYS,
} as const satisfies Readonly<Record<string, string>>;

/** The seven fields of one shape divider, as the flat key suffixes the AI surface spells them with. */
const DIVIDER_FIELDS = [
    ['', 'isEnabled'],
    ['View', 'view'],
    ['Color', 'color'],
    ['Height', 'height'],
    ['Repeat', 'repeat'],
    ['HorizontalFlip', 'horizontalFlip'],
    ['VerticalFlip', 'verticalFlip'],
] as const;

const DIVIDER_EDGES = [
    ['top', 'isTopShapeDivider', 'topShapeDivider'],
    ['bottom', 'isBottomShapeDivider', 'bottomShapeDivider'],
] as const;

const dividerKeys = (edge: (typeof DIVIDER_EDGES)[number]): string[] => [
    edge[1],
    ...DIVIDER_FIELDS.filter(([suffix]) => suffix !== '').map(([suffix]) => `${edge[2]}${suffix}`),
];

/**
 * How many divider shapes the host ships, per edge — `TOP_SHAPE_DIVIDERS` / `BOTTOM_SHAPE_DIVIDERS`
 * in `common/constants/shape-dividers.const.ts`, fifteen each.
 *
 * The field is a 1-based INDEX into those tables, and it was an unbounded string: the renderer does
 * `SHAPES[edge][flip][Number(view) - 1]`, so `"20"` reads past the end, hands `undefined` to the
 * markup slot and draws nothing. Valid-looking call, no error, no divider. The entity cannot catch
 * it either — it stores `view` as `z.string()`, deliberately, because the package is framework-free.
 * So the bound belongs here, on the surface that talks to the model.
 */
const SHAPE_DIVIDER_VIEWS = 15;

const dividerViewSchema = z.string().regex(/^([1-9]|1[0-5])$/, `a 1-based index from "1" to "${SHAPE_DIVIDER_VIEWS}"`);

const flipSchema = z.enum(['none', 'horizontal', 'vertical']);

export const blockPatchShape = {
    ...boxPatchShape,

    isFullWidth: z
        .boolean()
        .optional()
        .describe('Let the block span the whole viewport instead of the centred content column.'),
    isFullHeight: z.boolean().optional().describe('Make the block at least as tall as the viewport.'),
    height: z.number().optional().describe('Minimum block height in px. Omit for content-driven height.'),

    pin: z
        .enum(['top', 'bottom'])
        .nullable()
        .optional()
        .describe('Stick the block to the top or the bottom of the viewport while scrolling. null unpins it.'),
    syncWithDesktop: z
        .boolean()
        .optional()
        .describe('Write desktop style edits through to the tablet and mobile tabs.'),

    // Per-breakpoint visibility — the one group that is deliberately NOT written to all three
    // devices at once (see `props-patch.ts`), because naming one device IS the point here.
    hideOnDesktop: z.boolean().optional().describe('Hide the block on desktop widths. It still exists on the page.'),
    hideOnTablet: z.boolean().optional().describe('Hide the block on tablet widths. It still exists on the page.'),
    hideOnMobile: z.boolean().optional().describe('Hide the block on mobile widths. It still exists on the page.'),

    ...scrollEffectShape,

    isTopShapeDivider: z.boolean().optional().describe('Draw a decorative divider along the top edge.'),
    topShapeDividerView: dividerViewSchema
        .optional()
        .describe(`Which divider drawing: a 1-based index as a string, "1" to "${SHAPE_DIVIDER_VIEWS}".`),
    topShapeDividerColor: z.string().optional().describe(`Fill colour of the top divider shape.${THEME_COLOR_HINT}`),
    topShapeDividerHeight: z.number().optional().describe('Height of the top divider band, px.'),
    topShapeDividerRepeat: z
        .number()
        .optional()
        .describe('How many times the top divider shape tiles across the width. 1 stretches it once.'),
    topShapeDividerHorizontalFlip: flipSchema
        .optional()
        .describe('Mirror the top divider shape horizontally: "none" | "horizontal" | "vertical".'),
    topShapeDividerVerticalFlip: flipSchema
        .optional()
        .describe('Mirror the top divider shape vertically: "none" | "horizontal" | "vertical".'),
    isBottomShapeDivider: z.boolean().optional().describe('Draw a decorative divider along the bottom edge.'),
    bottomShapeDividerView: dividerViewSchema
        .optional()
        .describe(
            `Which divider drawing for the bottom edge: a 1-based index as a string, "1" to ` +
                `"${SHAPE_DIVIDER_VIEWS}".`
        ),
    bottomShapeDividerColor: z
        .string()
        .optional()
        .describe(`Fill colour of the bottom divider shape.${THEME_COLOR_HINT}`),
    bottomShapeDividerHeight: z.number().optional().describe('Height of the bottom divider band, px.'),
    bottomShapeDividerRepeat: z
        .number()
        .optional()
        .describe('How many times the bottom divider shape tiles across the width. 1 stretches it once.'),
    bottomShapeDividerHorizontalFlip: flipSchema
        .optional()
        .describe('Mirror the bottom divider shape horizontally: "none" | "horizontal" | "vertical".'),
    bottomShapeDividerVerticalFlip: flipSchema
        .optional()
        .describe('Mirror the bottom divider shape vertically: "none" | "horizontal" | "vertical".'),
} as const;

/**
 * The inverse of the invariant that used to live here, and it still fails the BUILD rather than a
 * test.
 *
 * These keys must NOT appear in the shape. Declaring one would advertise it to the model as a real
 * field — `z.unknown()` accepts anything — and cost the tool surface its bytes, which is what was
 * removed. They reach `validateForgiving` as `unknownKeyReasons` instead, so an unknown key still
 * comes back with its specific reason rather than a bare "unknown field".
 *
 * Reads as `true` while the two sets are disjoint; the moment one key appears in both, the type
 * becomes that key's name and the assignment stops compiling.
 */
type DeclaredIgnoredBlockKey = Extract<keyof typeof IGNORED_BLOCK_KEYS, keyof typeof blockPatchShape>;

const _noIgnoredBlockKeyIsDeclared: DeclaredIgnoredBlockKey extends never ? true : DeclaredIgnoredBlockKey = true;

const blockPatchSchema = z.object(blockPatchShape);
export type BlockPatch = BoxPatch & z.infer<typeof blockPatchSchema>;

/**
 * The cap on how many columns one block may be created with.
 *
 * Six is where the grid runs out of meaning rather than a taste judgement: `col` widths are twelfths,
 * so seven columns cannot divide the row evenly, and the editor's own column presets stop at three.
 *
 * Declared HERE rather than next to the scaffold that enforces it, because `block-scaffold.ts`
 * already imports this module — putting it there and importing it back would close a cycle, and this
 * side of it is the one every consumer already reaches for.
 *
 * NOT spelled as `.max()` on the field below, deliberately. The forgiving validator repairs what it
 * can and DELETES what it cannot, and a `too_big` is in the second group: `columns: 8` lost the
 * field entirely and built a ONE-column block — the opposite of what was asked, reported only as
 * "ignored: columns". The cap is applied where it can be reported as a normalization instead
 * (`fitColumnCount` in `apps/mcp-backend`), next to the `columnWidth` clamp that already works that
 * way, with `createBlockScaffold`'s own `Math.min` as the last line for callers that skip the schema.
 */
export const MAX_BLOCK_COLUMNS = 6;

export const createBlockArgsSchema = z.object({
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('Position within the page (0-based). Omit to append at the end. Ignored when `role` is set.'),
    role: z
        .enum(['header', 'footer'])
        .optional()
        .describe(
            "Make this block the site's header or footer instead of a block on this page. A site has " +
                'exactly one of each, they belong to the SITE, and they appear on EVERY page — so this ' +
                'creates something the user will see everywhere, and the call fails if the slot is already ' +
                'filled. Omit for an ordinary page block. A header always renders first and a footer last; ' +
                '`index` has no meaning for them.'
        ),
    columns: z
        .number()
        .int()
        .optional()
        .describe(
            `How many side-by-side columns the block starts with, 1 to ${MAX_BLOCK_COLUMNS} (1 by ` +
                'default; a number outside that is pulled to the nearest one that fits). The row is ' +
                'split evenly, and the result reports every column id — use them to fill the columns ' +
                'in the same batch. Ask for the columns here rather than adding them afterwards.'
        ),
    ...blockPatchShape,
});

export const updateBlockArgsSchema = z.object({
    blockId: z.string().describe('Id of the block to update.'),
    ...blockPatchShape,
});

/** The patch keys, i.e. everything a create/update call carries that isn't an id or a position. */
export const BLOCK_PATCH_KEYS: readonly string[] = Object.keys(blockPatchShape);

const HIDDEN_ON_KEYS = ['hideOnDesktop', 'hideOnTablet', 'hideOnMobile'] as const;
const SCROLL_EFFECT_KEYS = ['isScrollEffect', 'scrollEffect'] as const;

/** What a divider starts from when the stored one keeps only its switch — the editor's seed, off. */
const DIVIDER_SEED = () => ({ ...createShapeDivider(), isEnabled: false });

/**
 * Folds a flat AI patch onto a v0 block's `props`, returning the new `props` plus whatever was
 * accepted-but-not-stored. Groups the patch doesn't mention keep their stored object.
 *
 * The two booleans legacy used for size are unit choices in v0: `isFullWidth` is `100vw` (and `false`
 * is `null`, the centred content column), `isFullHeight` is `100vh`. `height` sets px instead, and
 * loses to `isFullHeight` when both arrive — a model that asks for both means the viewport.
 */
export function foldBlockPatch(props: PropsBag, patch: BlockPatch): { props: PropsBag } {
    const next = foldBoxPatch(props, patch);

    if (patch.isFullWidth !== undefined) {
        next['width'] = patch.isFullWidth ? { value: 100, unit: 'vw' } : null;
    }

    if (patch.isFullHeight !== undefined) {
        next['height'] = patch.isFullHeight ? everyDevice(() => ({ value: 100, unit: 'vh' })) : everyDevice(() => null);
    } else if (patch.height !== undefined) {
        next['height'] = everyDevice(() => ({ value: patch.height as number, unit: 'px' }));
    }

    for (const key of ['pin', 'syncWithDesktop'] as const) {
        if (patch[key] !== undefined) {
            next[key] = patch[key];
        }
    }

    if (mentions(patch, HIDDEN_ON_KEYS)) {
        next['hidden'] = {
            ...((props['hidden'] ?? { desktop: false, tablet: false, mobile: false }) as PropsBag),
            ...(patch.hideOnDesktop !== undefined ? { desktop: patch.hideOnDesktop } : {}),
            ...(patch.hideOnTablet !== undefined ? { tablet: patch.hideOnTablet } : {}),
            ...(patch.hideOnMobile !== undefined ? { mobile: patch.hideOnMobile } : {}),
        };
    }

    // A block's scroll effect is a responsive pair INSIDE its background — unlike the column's,
    // which is a flat feature. Two shapes that differ in what they can express, per `scroll-effect`.
    if (mentions(patch, SCROLL_EFFECT_KEYS)) {
        const background = { ...((next['background'] ?? {}) as PropsBag) };
        const scrollEffect = { ...((background['scrollEffect'] ?? {}) as PropsBag) };

        if (patch.isScrollEffect !== undefined) {
            scrollEffect['isEnabled'] = everyDevice(() => patch.isScrollEffect as boolean);
        }

        // A scroll effect that was off keeps only its switch; switching it on seeds its value.
        if (patch.isScrollEffect && !scrollEffect['value']) {
            scrollEffect['value'] = everyDevice(() => BACKGROUND_LAYER_SEEDS.scrollEffect.value);
        }

        if (patch.scrollEffect !== undefined) {
            scrollEffect['value'] = everyDevice(() => patch.scrollEffect as string);
        }

        background['scrollEffect'] = scrollEffect;
        next['background'] = background;
    }

    const dividers = { ...((props['shapeDividers'] ?? {}) as PropsBag) };
    let touchedDividers = false;

    for (const edge of DIVIDER_EDGES) {
        if (!mentions(patch, dividerKeys(edge))) {
            continue;
        }

        const [edgeKey, enabledKey, fieldPrefix] = edge;
        const fieldPatch: PropsBag = {};

        for (const [suffix, field] of DIVIDER_FIELDS) {
            const value = (patch as PropsBag)[suffix === '' ? enabledKey : `${fieldPrefix}${suffix}`];

            if (value !== undefined) {
                fieldPatch[field] = value;
            }
        }

        dividers[edgeKey] = patchEveryDevice(
            dividers[edgeKey],
            // A divider that was off keeps only its switch; the seed fills in what it does not store.
            (stored: PropsBag) => ({
                ...(stored['view'] === undefined ? DIVIDER_SEED() : {}),
                ...stored,
                ...fieldPatch,
            }),
            DIVIDER_SEED
        );
        touchedDividers = true;
    }

    if (touchedDividers) {
        next['shapeDividers'] = dividers;
    }

    return { props: compactBlockProps(next) };
}

/** True when the patch names at least one key the fold would act on — the "empty patch" guard. */
export function hasBlockPatch(patch: object): boolean {
    return mentions(patch, BLOCK_PATCH_KEYS);
}
